import './index.less';
import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import localStore from '@/commons/store/local-store';
import { graphicStore } from '@/commons/store/graphic-store';
import { TnEngineContext, TnEngineExtraContext, AxisT, ttf, font, ModelBase, Line } from 'pytha';
import { cmdRegister } from './cmd-register';
import { AmbientLight, Color, DirectionalLight, Mesh, MeshBasicMaterial, PointLight, Scene, SphereGeometry } from 'three';
import Viewport, { CAMERA_TYPE } from './viewport';
import genUUID from '@/commons/utils/gen-uuid';
import { MaskLoadingModal } from 'tncet-common';
import axios from 'axios';
import { CommonTabbarContext, SideContext, StoreyContext, SystemContext, emitter } from '@/App';
import { SystemStatus } from '@/commons/context/system-store';
import { Ucs } from '@/commons/interface/geometry/ucs';
import { findGlobalUcs, findUcsInProject } from '@/api/geometry/ucs';
import { findLayersInProjectUuid } from '@/api/geometry/layer';
import { Layer } from '@/commons/interface/geometry/layer';
import { findFontGlyphs, findFontTtfs, findFontsInGraphic } from '@/api/geometry/font';
import { Primitive } from '@/commons/interface/geometry/primitive';
import { createPrimitive, createPrimitiveMulti, deletePrimitivesInGraphic, findPrimitivesInGraphicByTypes } from '@/api/geometry/primitive';
import { getTextureUrl, getTextures } from '@/api/spec-common/texture';
import { Texture } from '@/commons/interface/geometry/texture';
import { BimBaseContext, ModelsInBimCommon, ModelsInBimStructure, ModelsInBuilding, ModelsInFire } from 'tnbimbase';
import { ModelsInStructure } from '@/passets/structure-engine/models';
import { ModelsInHvac } from '@/passets/hvac-engine/models';
import { CommonTabbarState, TabRoute } from '@/commons/components/common-tabbar';
import { TAB_TYPE } from '@/commons/enums/tab-type';
import { DEVICEBASE_SINGAL } from '@/passets/devicebase-engine/enums/devicebase-singal';
import { PYTHA_SINGAL } from '@/passets/pytha-engine/enums/pytha-signal';
import { MonixAddPrimitivesCommand } from '@/passets/pytha-engine/commands/MonixAddPrimitivesCommand';
import TnMessage from '@/commons/widget/message';
import { Sensor } from '@/commons/interface/device/sensor';
import { DataBox } from '@/commons/interface/device/data-box';
import { createDataBox, deleteDataBoxByPrimitiveUuid } from '@/api/maint/device/data-box';
import { createSensor, deleteSensorByPrimitiveUuid } from '@/api/maint/device/sensor';
import { DeviceComponentT } from '@/passets/devicebase-engine/models/DeviceComponentT';
import { Cable } from '@/commons/interface/device/cable';
import { DeviceLevelTableDict, DeviceModelTypes, getDeviceTypeLevels } from '@/commons/enums/model-type';
import { changeCableConnect, createCable, deleteCableByPrimitiveUuid } from '@/api/maint/device/cable';
import { CableT } from '@/passets/devicebase-engine/models/CableT';
import { MonixUpdatePrimitivesCommand } from '@/passets/pytha-engine/commands/MonixUpdatePrimitivesCommand';
import { Model } from '@/commons/interface/project/model';
import { ModelType } from '@/commons/enums/model-file-type';
import { useCmdRegisterEffect } from './cmd-register-effect';
import { findProjectListByUser, getProjectDetail } from '@/api/project/project';
import { findRoofSlabSectionByUuid } from '@/api/building/roof-slab-section';
import { RoofSlabSection } from '@/commons/interface/building/roof-slab-section';
import { findGutterSectionByUuid } from '@/api/building/gutter-section';
import { GutterSection } from '@/commons/interface/building/gutter-section';
import { StoreyStatus } from '@/commons/context/storey-store';
import { ModelsInArchiBase } from '@/passets/archibase-engine/models';
import { Emit_Event } from '@/commons/enums/emit-event';
import { Project } from '@/commons/interface/project/project';

interface Iprops {
    tabTitle?: string;
    tabClosable?: boolean;
}

export default function DrawPanel(props: Iprops) {
    const [finishInit, setFinishInit] = useState<boolean>(false);
    const { projectUuid, drawKey, roofSlabUuid, gutterUuid, modelUuid, branchUuid } = useParams();
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingTip, setLoadingTip] = useState<string>('');
    const [sceneUpdate, setSceneUpdate] = useState<string>(''); // 场景更新的信号
    const systemContext = useContext<SystemStatus>(SystemContext);
    const sideContext = useContext(SideContext);
    const tabbarContext = useContext<CommonTabbarState>(CommonTabbarContext);
    const drawRef = useRef(null);
    const isInit = useRef<boolean>(false);
    const storeyContext = useContext<StoreyStatus>(StoreyContext);

    const navigate = useNavigate();

    useEffect(() => {
        let tabUrl = `${TAB_TYPE.DRAW}/${drawKey}`;
        if (!!roofSlabUuid) {
            tabUrl = `${TAB_TYPE.ROOF_SLAB}/${roofSlabUuid}`;
        } else if (!!gutterUuid) {
            tabUrl = `${TAB_TYPE.GUTTER}/${gutterUuid}`;
        }
        let drawTab: TabRoute = {
            title: props.tabTitle ? props.tabTitle : "绘图区",
            url: tabUrl,
            closable: props.tabClosable != null ? props.tabClosable : false,
        }
        tabbarContext.pushRoute(drawTab);
    }, [drawKey, roofSlabUuid, gutterUuid])

    // 切换局部绘制页签时触发
    useEffect(() => {
        if (!roofSlabUuid && !gutterUuid && !drawKey) {
            return
        }
        if (isInit.current) {
            initModels(true);
        }
    }, [drawKey, roofSlabUuid, gutterUuid])

    // 监听信号
    useEffect(() => {
        if (!graphicStore?.extraContext?.getCurrentViewEditor()) return;
        // 特殊图元的变更（这里特指删除）
        graphicStore.extraContext.getCurrentViewEditor().listeners.signals.onEntitiesUpdatePosition.add(onSpecialEntityUpdatePosition);
        // 设备布置完成
        graphicStore?.extraContext?.getCurrentViewEditor()?.listeners.getSignal(DEVICEBASE_SINGAL.onAssembleDeviceFinish).add(onAssembleDeviceFinish);
        // 数据线缆布置完成
        graphicStore?.extraContext?.getCurrentViewEditor()?.listeners.getSignal(DEVICEBASE_SINGAL.onAssembleCableFinish).add(onAssembleCableFinish);
        // 特殊图元的位置移动变更（move、夹点）
        graphicStore?.extraContext?.getCurrentViewEditor()?.listeners.getSignal(PYTHA_SINGAL.onEntitiesMovePosition).add(onEntitiesMovePosition);
        // 修改数据线缆头/尾设备
        graphicStore?.extraContext?.getCurrentViewEditor()?.listeners.getSignal(DEVICEBASE_SINGAL.onUpadteCableEndFinish).add(onUpadteCableEndFinish);
        return () => {
            if (!graphicStore?.extraContext?.getCurrentViewEditor()) return;
            graphicStore.extraContext.getCurrentViewEditor().listeners.signals.onEntitiesUpdatePosition.remove(onSpecialEntityUpdatePosition);
            graphicStore?.extraContext?.getCurrentViewEditor()?.listeners.getSignal(DEVICEBASE_SINGAL.onAssembleDeviceFinish).remove(onAssembleDeviceFinish);
            graphicStore?.extraContext?.getCurrentViewEditor()?.listeners.getSignal(DEVICEBASE_SINGAL.onAssembleCableFinish).remove(onAssembleCableFinish);
            graphicStore?.extraContext?.getCurrentViewEditor()?.listeners.getSignal(PYTHA_SINGAL.onEntitiesMovePosition).remove(onEntitiesMovePosition);
            graphicStore?.extraContext?.getCurrentViewEditor()?.listeners.getSignal(DEVICEBASE_SINGAL.onUpadteCableEndFinish).remove(onUpadteCableEndFinish);
        }
    }, [graphicStore?.extraContext?.getCurrentViewEditor()])

    // 监听采集箱/传感器等图元的位置变化的处理
    const onSpecialEntityUpdatePosition = (deleteEntities: ModelBase[], isDelete: boolean) => {
        let devicePromiseList = [];
        // 采集箱
        let dataBoxEntities = deleteEntities?.filter(item => item.type === 'SerialT' || item.type === 'DvrT') || [];
        if (dataBoxEntities?.length > 0) {
            for (let item of dataBoxEntities) {
                if (!item?.extraData?.globalDataBoxId) continue;
                if (isDelete) {
                    devicePromiseList.push(() => deleteDataBoxByPrimitiveUuid(item?.uuid));
                } else {
                    let par: DataBox = {
                        primitiveUuid: item.uuid,
                        globalDataBoxId: item?.extraData?.globalDataBoxId,
                        projectUuid: projectUuid,
                    }
                    devicePromiseList.push(() => createDataBox(par));
                }
            }
        }
        // 传感器
        let sensorEntities = deleteEntities?.filter(item => item.type === 'AnemoclinographT' || item.type === 'FluviographT' || item.type === 'MonitorT') || [];
        if (sensorEntities?.length > 0) {
            for (let item of sensorEntities) {
                if (!item?.extraData?.globalSensorId) continue;
                if (isDelete) {
                    devicePromiseList.push(() => deleteSensorByPrimitiveUuid(item?.uuid));
                } else {
                    let par: Sensor = {
                        primitiveUuid: item.uuid,
                        globalSensorId: item?.extraData?.globalSensorId,
                        projectUuid: projectUuid,
                    }
                    devicePromiseList.push(() => createSensor(par));
                }
            }
        }

        // 数据线缆
        let cablePromiseList = [];
        let cableEntities = deleteEntities?.filter(item => item.type === 'CableT') || [];
        if (cableEntities?.length > 0) {
            const editor = graphicStore?.extraContext?.getCurrentViewEditor();
            for (let item of cableEntities) {
                if (!item?.extraData) continue;
                if (isDelete) {
                    cablePromiseList.push(() => deleteDataBoxByPrimitiveUuid(item?.uuid));
                    let lowDeviceEntity = editor?.entityDict[item?.extraData?.firstDeviceUuid];
                    if (lowDeviceEntity) {
                        (lowDeviceEntity as DeviceComponentT).connected = false;
                        cablePromiseList.push(() => createPrimitive(lowDeviceEntity.toJson()));
                        editor.addObject(lowDeviceEntity);
                    }
                } else {
                    const firstDevice = editor.entityDict[item?.extraData?.firstDeviceUuid]
                    const secondDevice = editor.entityDict[item?.extraData?.secondDeviceUuid]
                    if (!firstDevice || !secondDevice) continue;
                    let cable: Cable = {
                        primitiveUuid: item.uuid,
                        globalCableId: item?.extraData?.globalCableId || null,
                        projectUuid: projectUuid,
                        lowLevelTable: DeviceLevelTableDict[getDeviceTypeLevels(firstDevice?.type)],
                        lowLevelPrimitiveUuid: item?.extraData?.firstDeviceUuid,
                        highLevelTable: DeviceLevelTableDict[getDeviceTypeLevels(secondDevice?.type)],
                        highLevelPrimitiveUuid: item?.extraData?.secondDeviceUuid,
                    }
                    cablePromiseList.push(() => createCable(cable));
                    let lowDeviceEntity = editor?.entityDict[item?.extraData?.firstDeviceUuid];
                    if (lowDeviceEntity) {
                        (lowDeviceEntity as DeviceComponentT).connected = true;
                        cablePromiseList.push(() => createPrimitive(lowDeviceEntity.toJson()));
                        editor.addObject(lowDeviceEntity);
                    }
                }
            }
            graphicStore?.extraContext?.getCurrentViewEditor().listeners.signals.needRender.dispatch('redraw');
        }

        axios.all(devicePromiseList?.map(fn => fn())).then(res => {
            sideContext.needRenderDataBoxSide();
            sideContext.needRenderSensorSide();

            axios.all(cablePromiseList?.map(fn => fn())).then(res2 => {
            }).catch(error => { });
        }).catch(error => { }).finally(() => { });


        // todo 监测对象
        let monitorObjectEntities = deleteEntities?.filter(item => item?.extraData?.monitorObjectId != null) || [];
        if (monitorObjectEntities.length > 0) {

        }
    }

    // 设备布置完成的回调
    const onAssembleDeviceFinish = (e) => {
        let addPris = e.addPris;
        graphicStore.extraContext.getCurrentViewEditor().history.execute(new MonixAddPrimitivesCommand(
            addPris,
            () => addDeviceExecuteCallback(addPris),
            () => addDeviceUndoCallback(addPris)
        ))
    }

    // 设备布置command的执行回调
    const addDeviceExecuteCallback = (addPris: ModelBase[]) => {
        createPrimitiveMulti(addPris?.map((entity: ModelBase) => {
            if (entity.toJson) {
                return entity.toJson();
            }
            return null
        }), { modelUuid: modelUuid, projectUuid: projectUuid }).then(() => {
            // 修改布置的采集箱/传感器信息
            if (addPris[0].type === 'DvrT' || addPris[0].type === 'SerialT') {
                let globalDataBoxId = addPris[0].extraData?.globalDataBoxId || null;
                if (!globalDataBoxId) return;
                let primitiveUuid = addPris[0].uuid;
                let par: DataBox = {
                    primitiveUuid: primitiveUuid,
                    globalDataBoxId: globalDataBoxId,
                    projectUuid: projectUuid,
                }
                createDataBox(par).then(res => {
                    if (res.status === 200) {
                        TnMessage.success("布置成功");
                    }
                }).catch(err => {
                    TnMessage.error("布置失败");
                })
            } else if (addPris[0].type === 'AnemoclinographT' || addPris[0].type === 'FluviographT' || addPris[0].type === 'MonitorT') {
                let globalSensorId = addPris[0].extraData?.globalSensorId || null;
                if (!globalSensorId) return;
                let primitiveUuid = addPris[0].uuid;
                let par: Sensor = {
                    primitiveUuid: primitiveUuid,
                    globalSensorId: globalSensorId,
                    projectUuid: projectUuid,
                }
                createSensor(par).then(res => {
                    if (res.status === 200) {
                        TnMessage.success("布置成功");
                    }
                }).catch(err => {
                    TnMessage.error("绑定失败");
                })
            }

        })
    }

    // 设备布置command的撤回回调
    const addDeviceUndoCallback = (addPris: ModelBase[]) => {
        deletePrimitivesInGraphic(addPris?.map(entity => entity.uuid), projectUuid).then(res => {
            if (addPris[0].type === 'DvrT' || addPris[0].type === 'SerialT') {
                deleteDataBoxByPrimitiveUuid(addPris[0].uuid).then(res => {
                    if (res.status === 200) {
                        TnMessage.success("取消布置成功");
                    }
                }).catch(err => {
                    TnMessage.error("解绑失败");
                })
            } else if (addPris[0].type === 'AnemoclinographT' || addPris[0].type === 'FluviographT' || addPris[0].type === 'MonitorT') {
                deleteSensorByPrimitiveUuid(addPris[0].uuid).then(res => {
                    if (res.status === 200) {
                        TnMessage.success("取消布置成功");
                    }
                }).catch(err => {
                    TnMessage.error("解绑失败");
                })
            }

        });
    }

    // 数据线布置完成的回调
    const onAssembleCableFinish = (e) => {
        let addPris = e.addPris;
        let firstDevice = e.firstDevice;
        let secondDevice = e.secondDevice;
        graphicStore.extraContext.getCurrentViewEditor().history.execute(new MonixAddPrimitivesCommand(
            addPris,
            () => addCableExecuteCallback(addPris, firstDevice, secondDevice),
            () => addCableUndoCallback(addPris, firstDevice, secondDevice)
        ))
    }

    /**
     * 添加数据线执行回调
     * @param addPris 数据线对象数组
     * @param firstDevice 较低级设图元对象
     * @param secondDevice 较高级设图元对象
     */
    const addCableExecuteCallback = (addPris: ModelBase[], firstDevice: DeviceComponentT, secondDevice: DeviceComponentT) => {
        let cableEntity = addPris[0];
        let lowLevelTable = DeviceLevelTableDict[getDeviceTypeLevels(firstDevice.type)];
        let highLevelTable = DeviceLevelTableDict[getDeviceTypeLevels(secondDevice.type)];
        if (!cableEntity || !lowLevelTable || !highLevelTable || !firstDevice?.uuid || !secondDevice?.uuid) {
            TnMessage.error("布置失败");
            return;
        }
        let firstClone = firstDevice.clone();
        firstClone.uuid = firstDevice.uuid;
        firstClone.connected = true;
        let par = [cableEntity, firstClone];

        let cable: Cable = {
            primitiveUuid: cableEntity.uuid,
            globalCableId: cableEntity?.extraData?.globalCableId || null,
            projectUuid: projectUuid,
            lowLevelTable: lowLevelTable,
            lowLevelPrimitiveUuid: firstDevice.uuid,
            highLevelTable: highLevelTable,
            highLevelPrimitiveUuid: secondDevice.uuid,
        }

        let priPromiseList = [];
        priPromiseList.push(createPrimitiveMulti(par?.map(e => e?.toJson() || null), { modelUuid: modelUuid, projectUuid: projectUuid }));
        priPromiseList.push(createCable(cable));
        axios.all(priPromiseList).then(() => {
            TnMessage.success("布置成功");
        }).catch(err => {
            console.log('err', err)
            TnMessage.error("布置失败");
        })
    }

    /**
     * 添加数据线撤回回调
     * @param addPris 数据线对象数组
     * @param firstDevice 较低级设图元对象
     * @param secondDevice 较高级设图元对象
     */
    const addCableUndoCallback = (addPris: ModelBase[], firstDevice: DeviceComponentT, secondDevice: DeviceComponentT) => {
        let priPromiseList = [];
        // 图元：删除线缆图元，修改较低级设备连接状态
        priPromiseList.push(deletePrimitivesInGraphic(addPris?.map(entity => entity.uuid), projectUuid));
        let firstClone = firstDevice.clone();
        firstClone.uuid = firstDevice.uuid;
        firstClone.connected = false;
        priPromiseList.push(createPrimitiveMulti([firstClone.toJson()], { modelUuid: modelUuid, projectUuid: projectUuid }));
        // 业务：删除线缆对象，置空较低级设备外键
        priPromiseList.push(deleteCableByPrimitiveUuid(addPris[0].uuid));
        axios.all(priPromiseList).then(() => {
            TnMessage.success("取消布置成功");
        }).catch(err => {
            TnMessage.error("取消布置失败");
        })
    }

    // 特殊图元位置移动的回调
    const onEntitiesMovePosition = (moveEntities: DeviceComponentT[]) => {
        // 采集箱
        let deviceEntities: DeviceComponentT[] = moveEntities?.filter(item => DeviceModelTypes.some(t => t === item.type)) || [];
        if (deviceEntities?.length > 0) {
            const editor = graphicStore?.extraContext?.getCurrentViewEditor();
            // 获取extraData中，firstDeviceUuid 或者 secondDeviceUuid 为这个的
            let needModifyCables: CableT[] = editor.getEntities().filter(item => item.type === 'CableT'
                && item?.extraData?.firstDeviceUuid
                && item?.extraData?.secondDeviceUuid
                && deviceEntities.some(de => de.uuid === item.extraData.firstDeviceUuid || de.uuid === item.extraData.secondDeviceUuid)) || [];
            // if (needModifyCables?.length === 0) {
            //     editor.listeners.signals.needRender.dispatch('redraw');
            //     return;
            // }
            for (let cable of needModifyCables) {
                let idx1 = deviceEntities.findIndex(de => de.uuid === cable.extraData.firstDeviceUuid);
                if (idx1 !== -1) {
                    // 修改起点
                    let entity: DeviceComponentT = deviceEntities[idx1];
                    let geoCurvesAtfer = [...cable._geoCurves];
                    (geoCurvesAtfer[0] as Line).start.set(entity.position.x, entity.position.y, entity.position.z);
                    cable.setCurves(geoCurvesAtfer);
                    cable.needRedraw = true;
                    cable.getBoundingBox();
                }

                let idx2 = deviceEntities.findIndex(de => de.uuid === cable.extraData.secondDeviceUuid);
                if (idx2 !== -1) {
                    // 修改终点
                    let entity: DeviceComponentT = deviceEntities[idx2];
                    let geoCurvesAtfer = [...cable._geoCurves];
                    (geoCurvesAtfer[cable._geoCurves.length - 1] as Line).end.set(entity.position.x, entity.position.y, entity.position.z);
                    cable.setCurves(geoCurvesAtfer);
                    cable.needRedraw = true;
                    cable.getBoundingBox();
                }
            }
            editor.addObjects([...needModifyCables], true);
            editor.listeners.signals.needRender.dispatch('redraw');
            createPrimitiveMulti(needModifyCables.map(entity => {
                if (entity?.toJson) {
                    return entity.toJson();
                }
                return null
            }), { modelUuid: modelUuid, projectUuid: projectUuid })

        }
    }

    // 修改数据线缆头/尾设备的回调
    const onUpadteCableEndFinish = (cable: CableT, location: string, level: number, deviceEntity: DeviceComponentT) => {
        let firstDevice: DeviceComponentT = graphicStore.extraContext.getCurrentViewEditor().entityDict[cable.extraData.firstDeviceUuid] as DeviceComponentT;
        let secondDevice: DeviceComponentT = graphicStore.extraContext.getCurrentViewEditor().entityDict[cable.extraData.secondDeviceUuid] as DeviceComponentT;
        let entities = [];
        let entitiesNew = [];
        graphicStore.extraContext.getCurrentViewEditor().selectControl.resetSelectedEntities();
        if (location === 'start') {
            let cableNew = cable.clone();
            cableNew.uuid = cable.uuid;
            cableNew.extraData = {
                ...cable.extraData,
                firstDeviceUuid: deviceEntity.uuid,
            }
            // 修改头节点位置
            let geoCurvesAtfer = [...cable._geoCurves];
            (geoCurvesAtfer[0] as Line).start.set(deviceEntity.position.x, deviceEntity.position.y, deviceEntity.position.z);
            cableNew.setCurves(geoCurvesAtfer);
            cableNew.needRedraw = true;
            cableNew.getBoundingBox();
            cable.needRedraw = true;


            let firstDeviceNew = firstDevice.clone();
            firstDeviceNew.uuid = firstDevice.uuid;
            firstDeviceNew.connected = false;

            let deviceEntityNew = deviceEntity.clone();
            deviceEntityNew.uuid = deviceEntity.uuid;
            deviceEntityNew.connected = true;
            entities = [cable, firstDevice, deviceEntity];
            entitiesNew = [cableNew, firstDeviceNew, deviceEntityNew];
            graphicStore.extraContext.getCurrentViewEditor().history.execute(new MonixUpdatePrimitivesCommand(
                entities,
                entitiesNew,
                () => updateCableEndCallback(location, level, deviceEntity, cableNew),
                () => updateCableEndCallback(location, level, firstDevice, cableNew),
            ))
        } else if (location === 'end') {
            let cableNew = cable.clone();
            cableNew.uuid = cable.uuid;
            cableNew.extraData = {
                ...cable.extraData,
                secondDeviceUuid: deviceEntity.uuid,
            }
            // 修改尾节点位置
            let geoCurvesAtfer = [...cable._geoCurves];
            (geoCurvesAtfer[cableNew._geoCurves?.length - 1] as Line).end.set(deviceEntity.position.x, deviceEntity.position.y, deviceEntity.position.z);
            cableNew.setCurves(geoCurvesAtfer);
            cableNew.needRedraw = true;
            cableNew.getBoundingBox();
            cable.needRedraw = true;
            entities = [cable];
            entitiesNew = [cableNew];
            graphicStore.extraContext.getCurrentViewEditor().history.execute(new MonixUpdatePrimitivesCommand(
                entities,
                entitiesNew,
                () => updateCableEndCallback(location, level, deviceEntity, cableNew),
                () => updateCableEndCallback(location, level, secondDevice, cableNew),
            ))
        }
    }

    /**
     * 外键可以通过level+primitiveUuid算出来
     * @param location 用来判断修改的是low部分还是high部分
     * @param level 确定数据表
     * @param newDeivceEntity 新连接的图元
     */
    const updateCableEndCallback = (location: string, level: number, newDeivceEntity: DeviceComponentT, cableNew: CableT) => {
        graphicStore.extraContext.getCurrentViewEditor().listeners.signals.needRender.dispatch('redraw');
        createPrimitive(cableNew.toJson()).then(res => { })
        // 表名
        let tableName = DeviceLevelTableDict[getDeviceTypeLevels(newDeivceEntity.type)];
        let par: Cable = {};
        if (location === 'start') {
            // 修改头夹点，即修改low部分
            par = {
                primitiveUuid: cableNew.uuid,
                lowLevelTable: tableName,
                lowLevelPrimitiveUuid: newDeivceEntity.uuid,
            }
        } else if (location === 'end') {
            // 修改尾夹点，即修改high部分
            par = {
                primitiveUuid: cableNew.uuid,
                highLevelTable: tableName,
                highLevelPrimitiveUuid: newDeivceEntity.uuid,
            }
        }
        changeCableConnect(par).then(res => { }).catch(err => { })
    }


    if (graphicStore.context == null) {
        graphicStore.context = TnEngineContext.getInstance();
    }

    if (graphicStore.extraContext == null) {
        graphicStore.extraContext = TnEngineExtraContext.getInstance();
    }

    if (graphicStore.bimContext == null) {
        graphicStore.bimContext = BimBaseContext.getInstance();
    }

    const editor = graphicStore.extraContext.getCurrentViewEditor();
    // const [block, setBlock] = useState<Block>();
    const initScene = () => {
        return new Promise((resolve, reject) => {
            if (graphicStore.extraContext.sceneContext.scene == null) {
                let scene = new Scene();

                // 在初始化部分添加光源
                // 添加光照
                const ambientLight = new AmbientLight(0xffffff, 1);
                scene.add(ambientLight);

                const directionalLight = new DirectionalLight(0xffffff, 1);
                directionalLight.position.set(1, 0, 0).normalize();
                directionalLight.castShadow = true; // 使光源投射阴影
                scene.add(directionalLight);

                const directionalLight1 = new DirectionalLight(0xffffff, 1);
                directionalLight1.position.set(-1, 0, 0).normalize();
                directionalLight1.castShadow = true; // 使光源投射阴影
                scene.add(directionalLight1);
                const directionalLight2 = new DirectionalLight(0xffffff, 1);
                directionalLight2.position.set(0, 1, 0).normalize();
                directionalLight2.castShadow = true; // 使光源投射阴影
                scene.add(directionalLight2);
                const directionalLight3 = new DirectionalLight(0xffffff, 1);
                directionalLight3.position.set(0, -1, 0).normalize();
                directionalLight3.castShadow = true; // 使光源投射阴影
                scene.add(directionalLight3);
                const directionalLight4 = new DirectionalLight(0xffffff, 1);
                directionalLight4.position.set(0, 0, 1).normalize();
                directionalLight4.castShadow = true; // 使光源投射阴影
                scene.add(directionalLight4);

                // const directionalLight2 = new DirectionalLight(0xffffff, 0.7);
                // directionalLight2.position.set(1, 3, 1).normalize();
                // directionalLight2.castShadow = true; // 使光源投射阴影
                // scene.add(directionalLight2);
                const sphere = new SphereGeometry(0.5, 16, 8);
                const pointLight1 = new PointLight(0xffaa00, 4000, 1000000)
                pointLight1.position.set(0, 0, 0)
                pointLight1.add(new Mesh(sphere, new MeshBasicMaterial({ color: 0xff0040 })));
                scene.add(pointLight1)

                // // 创建一个光源帮助器（可选）
                // const pointLightHelper = new PointLightHelper(pointLight1, 0.5);
                // scene.add(pointLightHelper);

                graphicStore.extraContext.sceneContext.scene = scene;

                setSceneUpdate(genUUID());
                resolve(1);
            }
        })
    }

    useEffect(() => {
        emitter.on(Emit_Event.UPDATE_MODELS, showLoadingInitModels)
        emitter.on(Emit_Event.LOADING_DXF, loadingDxf)
        return (() => {
            emitter.off(Emit_Event.UPDATE_MODELS, showLoadingInitModels)
            emitter.off(Emit_Event.LOADING_DXF, loadingDxf)
        })
    }, [])

    const loadingDxf = (visible) => {
        setLoading(visible)
        setLoadingTip("正在导入dxf文件")
    }

    const showLoadingInitModels = () => {
        generateData()
    }

    // 将命令api注册到图形库中
    useCmdRegisterEffect(systemContext, storeyContext,
        [systemContext.currentModelUuid, systemContext.currentProjectUuid, storeyContext.currentStorey]);
    // useCmdRegisterEffect(systemContext, [systemContext.currentBranchUuid, systemContext.currentModelUuid, systemContext.currentProjectUuid]);

    useEffect(() => {
        init();
        return (() => {
            window.removeEventListener('resize', onWindowResize);
            // if (graphicStore.extraContext && graphicStore.extraContext.sceneContext
            //     && graphicStore.extraContext.sceneContext.scene) {
            //     graphicStore.extraContext.sceneContext.scene.clear();
            //     graphicStore.extraContext.sceneContext.scene = null;
            // }
        })
    }, [projectUuid])

    useEffect(() => {
        if (!projectUuid) {
            localStore.projectUuid = null;
            systemContext.setCurrentProjectUuid(null);
            return () => { };
        }
        if (!modelUuid) {
            localStore.modelUuid = null;
            systemContext.setCurrentModelUuid(null);
            return () => { };
        }
        // 模型变化，更新数据
        generateData();
    }, [projectUuid, modelUuid, branchUuid, systemContext.updateDrawPanel])

    const init = () => {
        localStore.projectUuid = projectUuid;
        initScene();
        document.oncontextmenu = () => {
            return false;
        }
        if (graphicStore?.context) {
            Object.keys(ModelsInBimCommon).forEach(type => {
                graphicStore.context.modelContext.addEntitytoCtrl(type);
                graphicStore.context.modelContext.registerModel(type, ModelsInBimCommon[type]);
            })
            Object.keys(ModelsInBimStructure).forEach(type => {
                graphicStore.context.modelContext.addEntitytoCtrl(type);
                graphicStore.context.modelContext.registerModel(type, ModelsInBimStructure[type]);
            })
            Object.keys(ModelsInArchiBase).forEach(type => {
                graphicStore.context.modelContext.addEntitytoCtrl(type);
                graphicStore.context.modelContext.registerModel(type, ModelsInArchiBase[type]);
            })
            Object.keys(ModelsInBuilding).forEach(type => {
                graphicStore.context.modelContext.addEntitytoCtrl(type);
                graphicStore.context.modelContext.registerModel(type, ModelsInBuilding[type]);
            })
            Object.keys(ModelsInFire).forEach(type => {
                graphicStore.context.modelContext.addEntitytoCtrl(type);
                graphicStore.context.modelContext.registerModel(type, ModelsInFire[type]);
            })
        }
        window.addEventListener('resize', onWindowResize);
    }

    const onWindowResize = () => {
        graphicStore.extraContext.listeners?.signals.windowResize.dispatch();
    }

    const generateData = () => {
        if (!projectUuid) return;
        // 判断人员是否在项目中
        findProjectListByUser().then(res => {
            let projectList: Project[] = res.data || []
            if (projectList?.find(item => item.uuid === projectUuid)) {
                setLoading(true);
                setFinishInit(false);
                // 设置世界坐标的默认颜色
                graphicStore.context.renderContext.axisColor.set(new Color(0, 0, 0))
                // 设置鼠标默认颜色
                graphicStore.context.renderContext.cusorColor.set(new Color(0, 0, 1))
                // 显示边线
                graphicStore.context.renderContext.conceptShowLine = true
                localStore.projectUuid = projectUuid;
                systemContext.setCurrentProjectUuid(projectUuid);
                storeyContext.setAutoGroupStorey(false);
                initUcs().then((_) => {
                    return initLayers();
                }).then((_) => {
                    return initFonts();
                }).then((_) => {
                    return initTexture();
                }).then((_) => {
                    return initModels();
                }).finally(() => {
                    // if (systemContext.currentCameraType === CAMERA_TYPE.Orthographic) {
                    //     ocFinally();
                    // } else if (systemContext.currentCameraType === CAMERA_TYPE.Perspective) {
                    //     prFinally();
                    // }
                    graphicStore.extraContext.getCurrentViewEditor()?.showAllEntities(true);
                    // TODO 全局设置
                    graphicStore.context.renderContext.gridLineDistanceToCircle = 3000;
                    graphicStore.context.renderContext.textHeight = 300;
                    graphicStore.extraContext.listeners.signals.onEditStatusChanged.dispatch();
                    setLoading(false);
                    storeyContext.setAutoGroupStorey(true);
                    setFinishInit(true);
                })
            } else {
                // 跳转到项目列表
                navigate("/desktop")
            }
        })

    }



    const initUcs = () => {
        return new Promise((resolve, reject) => {
            setLoadingTip('正在加载坐标系');
            findUcsInProject(projectUuid, modelUuid, branchUuid).then(res => {
                let jsonList: Ucs[] = res.data || [];
                let ucsList = [];
                jsonList.forEach((json) => {
                    if (json.isWcs) return;
                    let ucs: AxisT = new AxisT().fromJson(json);
                    ucsList.push(ucs);
                });
                findGlobalUcs(projectUuid, modelUuid).then(res => {
                    let json = res.data;
                    let wcs = new AxisT().fromJson(json);
                    if (wcs != null) {
                        ucsList.push(wcs);
                    }
                    if (wcs == null && ucsList.length > 0) wcs = ucsList[0];
                    graphicStore.context.ucsContext.setUcsList(ucsList);
                    graphicStore.context.ucsContext.setCurrentUcs(wcs);
                    if (wcs != null) {
                        let viewObj = wcs.render();
                        graphicStore.extraContext.sceneContext.scene?.add(viewObj);
                    }
                    resolve(1);
                })
            })
        })
    }

    const initLayers = () => {
        return new Promise((resolve, reject) => {
            setLoadingTip('正在加载图层');
            findLayersInProjectUuid(projectUuid, modelUuid, branchUuid).then(res => {
                let layers: Layer[] = res.data || [];
                layers.forEach((layer) => {
                    layer.lock = false;
                    layer.hidden = false;
                });
                let originLayer = layers.find((layer) => layer.originLayer && layer.projectUuid == projectUuid);
                if (originLayer == null) originLayer = layers[0];
                if (originLayer != null) {
                    graphicStore.context.layerContext.setCurrentLayerUuid(originLayer.uuid);
                    graphicStore.context.layerContext.setCurrentLayerColor(originLayer.color);
                    graphicStore.context.layerContext.setCurrentColor(originLayer.color);
                }
                graphicStore.context.layerContext.setLayers([...layers]);
                resolve(1);
            })
        });
    };

    const initFonts = () => {
        return new Promise((resolve, reject) => {
            setLoadingTip('正在加载文字样式');
            // 初始化时加载标注所用字体及默认字体
            if (!!graphicStore.extraContext.fontContext.state.dimFontData && !!graphicStore.extraContext.fontContext.state.currentFont) {
                resolve(1);
            }
            let ttfPromise = findFontTtfs();
            let fontPromise = findFontsInGraphic(projectUuid, modelUuid, branchUuid);
            // 加载0~9和.所需要的glyphs
            let params = {
                unicodeFile: "times.ttf",
                rangeStart: 0,
                rangeEnd: 1919,
                bakFile: "stsong.ttf",
                text: '0123456789. \\:;/*=+-_abcdefghigklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ',
            };
            let glyphPromise = findFontGlyphs(params);
            axios.all([ttfPromise, fontPromise, glyphPromise]).then((res) => {
                let ttfList: ttf[] = res[0].data || [];
                graphicStore.extraContext.fontContext.generateTtfNameList(ttfList);
                let fontList: font[] = res[1].data || [];
                let defaultFont;
                fontList.forEach((font) => {
                    let ttf = ttfList.find(
                        (item) => item.name === font.fontname && item.style === font.style
                    );
                    font.ttf = ttf;
                    if (font.originFont) {
                        defaultFont = font;
                    }
                });
                graphicStore.extraContext.fontContext.setFontList(fontList);
                if (!!defaultFont) {
                    graphicStore.extraContext.fontContext.setCurrentFont(defaultFont);
                }
                graphicStore.extraContext.fontContext.setDimFontData(res[2].data);
                resolve(1);
            });
        });
    }

    const initTexture = () => {
        return new Promise((resolve, reject) => {
            setLoadingTip('正在加载纹理/材质');
            getTextures(projectUuid).then(res => {
                let textures: Texture[] = res.data || [];
                textures.forEach(texture => {
                    if (texture && texture.uuid && texture.url) {
                        let imageUrl = getTextureUrl(texture.url)
                        graphicStore.extraContext.materialContext.materialUrlMap[texture.uuid] = imageUrl;
                        // convertImageToBase64(imageUrl).then(res => {
                        //     graphicStore.extraContext.materialContext.materialBase64Map[texture.uuid] = res
                        // })
                    }
                })
                resolve(1);
            })
        });
    }
    // TODO: 根据url获取base64，待解除 
    // async function convertImageToBase64(url) {
    //     let response = await fetch(url)
    //     let blob = await response.blob()
    //     return new Promise((resolve, reject) => {
    //         let reader = new FileReader()
    //         reader.onloadend = () => {
    //             let res = reader.result
    //             resolve(res)
    //         }
    //         reader.onerror = reject;
    //         reader.readAsDataURL(blob);
    //     })
    // }


    useEffect(() => {
        if (!systemContext.rws) return;
        systemContext.rws.onmessage = (value) => {
            const body = JSON.parse(value.data);
            switch (body.type) {
                case "POST":
                    let json = body.data;
                    let entity: ModelBase = new (graphicStore.context.modelContext.getModelConstructor(json.type))();
                    entity.fromJson(json);
                    editor.addObject(entity);
                    editor.listeners.signals.needRender.dispatch("redraw");
                    break;
                case "MULTI_POST":
                    let jsonList: any[] = body.data;
                    jsonList?.forEach((json) => {
                        let entity: ModelBase = new (graphicStore.context.modelContext.getModelConstructor(json.type))();
                        entity.fromJson(json);
                        editor.addObject(entity);
                    });
                    editor.listeners.signals.needRender.dispatch("redraw");
                    break;
                case "DELETE":
                    let uuids: string[] = body.data;
                    uuids.forEach((uuid) => {
                        editor.removeObjectByUuid(uuid);
                    });
                    editor.listeners.signals.needRender.dispatch("redraw");
                    break;
                case "LAYER_POST":
                    let layer: Layer = body.data;
                    let item = graphicStore.context.layerContext.layers.find(item => item.uuid === layer.uuid);
                    if (!item) {
                        layer = {
                            ...layer,
                            lock: false,
                            hidden: false,
                            originLayer: false,
                        };
                        graphicStore.context.layerContext.addLayer(layer);
                    } else {
                        item.color = layer.color;
                        item.lineWidth = layer.lineWidth;
                        item.name = layer.name;
                        graphicStore.context.layerContext.flush();
                        let entities = editor.getEntities()?.filter(e => e.layerUuid === layer.uuid && e.isColorByLayer);
                        entities.forEach(e => {
                            let newE = editor.entityDict[e.uuid];
                            newE.changeColor(layer.color, true);
                        })
                        editor.listeners.signals.needRender.dispatch("redraw");
                    }
                    break;
                case "LAYER_DELETE":
                    let deleteLayer: Layer = body.data;
                    graphicStore.context.layerContext.deleteLayer(deleteLayer);
                    break;
                case "UCS_POST":
                    let ucsJson = body.data;
                    let ucs: AxisT = new AxisT().fromJson(ucsJson);
                    let tmp: AxisT = graphicStore.context.ucsContext.ucsList.find(item => item.uuid === ucs.uuid);
                    if (tmp != null) {
                        tmp.fromJson(ucsJson);
                        tmp.updateViewGeometry();
                    } else {
                        graphicStore.context.ucsContext.addUcs(ucs, false);
                    }
                    editor.listeners.signals.needRender.dispatch();
                    break;
                case "UCS_DELETE":
                    let deleteUcsJson = body.data;
                    graphicStore.context.ucsContext.removeUcsByUuid(deleteUcsJson.uuid);
                    break;
            }
        }
    }, [systemContext.rws])

    // useEffect(() => {
    //     if (!finishInit) return;
    //     setLoading(true);
    //     initModels().finally(() => {
    //         graphicStore.extraContext.getCurrentViewEditor()?.showAllEntities(true);
    //         graphicStore.extraContext.getCurrentViewEditor()?.selectControl.resetSelectedEntities();
    //         setLoading(false);
    //     })
    // }, [systemContext.activeModelUuid, systemContext.updateModel, storeyContext.currentStorey])
    useEffect(() => {
        if (!finishInit) return;
        setLoading(true);
        initModels().finally(() => {
            graphicStore.extraContext.getCurrentViewEditor()?.showAllEntities(true);
            graphicStore.extraContext.getCurrentViewEditor()?.selectControl.resetSelectedEntities();
            setLoading(false);
        })
    }, [systemContext.updateModel, storeyContext.currentStorey])

    const initModels = (isNotGen?: boolean) => {
        return new Promise((resolve, reject) => {
            if (!!drawKey) {
                setLoadingTip('正在加载模型');
                if (isNotGen) {
                    setLoading(true);
                }
                //将tnbimbase项目中的额外图元注册进去
                if (graphicStore.context) {
                    Object.keys(ModelsInBimCommon).forEach(type => {
                        graphicStore.context.modelContext.addEntitytoCtrl(type);
                        graphicStore.context.modelContext.registerModel(type, ModelsInBimCommon[type]);
                    })
                    Object.keys(ModelsInStructure).forEach(type => {
                        graphicStore.context.modelContext.addEntitytoCtrl(type);
                        graphicStore.context.modelContext.registerModel(type, ModelsInStructure[type]);
                    })
                    Object.keys(ModelsInHvac).forEach(type => {
                        graphicStore.context.modelContext.addEntitytoCtrl(type);
                        graphicStore.context.modelContext.registerModel(type, ModelsInHvac[type]);
                    })
                    Object.keys(ModelsInBimStructure).forEach(type => {
                        graphicStore.context.modelContext.addEntitytoCtrl(type);
                        graphicStore.context.modelContext.registerModel(type, ModelsInBimStructure[type]);
                    })
                    Object.keys(ModelsInBuilding).forEach(type => {
                        graphicStore.context.modelContext.addEntitytoCtrl(type);
                        graphicStore.context.modelContext.registerModel(type, ModelsInBuilding[type]);
                    })
                    Object.keys(ModelsInArchiBase).forEach(type => {
                        graphicStore.context.modelContext.addEntitytoCtrl(type);
                        graphicStore.context.modelContext.registerModel(type, ModelsInArchiBase[type]);
                    })
                    Object.keys(ModelsInFire).forEach(type => {
                        graphicStore.context.modelContext.addEntitytoCtrl(type);
                        graphicStore.context.modelContext.registerModel(type, ModelsInFire[type]);
                    })
                };
                let firstParam = {
                    types: ['HoleT', 'DoorT', 'WindowT'],
                    storeyUuid: storeyContext.currentStorey?.uuid,
                    modelUuid: modelUuid,
                    branchUuid: branchUuid,
                }
                findPrimitivesInGraphicByTypes(projectUuid, firstParam).then((res) => {
                    if (graphicStore.extraContext.getCurrentViewEditor()) {
                        let jsonList: Primitive[] = res.data || [];
                        let entities = jsonList.map((json: Primitive) => {
                            let entity: ModelBase = new (graphicStore.context.modelContext.getModelConstructor(json.properties.type))();
                            entity.fromJson(json.properties);
                            return entity;
                        });
                        graphicStore.extraContext.getCurrentViewEditor().clearEntityDict();
                        graphicStore.extraContext.getCurrentViewEditor().addObjects(entities, false);
                    }
                    isInit.current = true
                    let secondParam = {
                        notTypes: ['HoleT', 'DoorT', 'WindowT'],
                        storeyUuid: storeyContext.currentStorey?.uuid,
                        modelUuid: modelUuid,
                        branchUuid: branchUuid,
                    }
                    return findPrimitivesInGraphicByTypes(projectUuid, secondParam)
                }).then((res) => {
                    if (graphicStore.extraContext.getCurrentViewEditor()) {
                        let jsonList: Primitive[] = res.data || [];
                        let entities = jsonList.map((json: Primitive) => {
                            let entity: ModelBase = new (graphicStore.context.modelContext.getModelConstructor(json.properties.type))();
                            entity.fromJson(json.properties);
                            return entity;
                        });
                        graphicStore.extraContext.getCurrentViewEditor().addObjects(entities, false);
                    }
                    resolve(1);
                }).finally(() => {
                    graphicStore.extraContext.getCurrentViewEditor().listeners.signals.needRender.dispatch('redraw');
                    if (isNotGen) {
                        setLoading(false);
                    }
                })
            } else if (!!roofSlabUuid || !!gutterUuid) {
                graphicStore.extraContext.getCurrentViewEditor().clearEntityDict();
                graphicStore.extraContext.getCurrentViewEditor().renderControl.clearObjectsStates();
                // 这里获取一下屋面板或者天沟的entity
                if (!!roofSlabUuid) {
                    findRoofSlabSectionByUuid(roofSlabUuid).then(roofRes => {
                        let roof: RoofSlabSection = roofRes.data || null;
                        if (roof?.entity) {
                            let entity: ModelBase = new (graphicStore.context.modelContext.getModelConstructor(roof.entity.type))();
                            entity.fromJson(roof.entity);
                            graphicStore.extraContext.getCurrentViewEditor().addObjects([entity]);
                        }
                        resolve(1);
                        isInit.current = true
                    })
                } else if (!!gutterUuid) {
                    findGutterSectionByUuid(gutterUuid).then(gutterRes => {
                        let gutter: GutterSection = gutterRes.data || null;
                        if (gutter?.entity) {
                            let entity: ModelBase = new (graphicStore.context.modelContext.getModelConstructor(gutter.entity.type))();
                            entity.fromJson(gutter.entity);
                            graphicStore.extraContext.getCurrentViewEditor().addObjects([entity]);
                        }
                        resolve(1);
                        isInit.current = true
                    })
                }
            }
        });
    }


    return (
        <div className="draw-panel" ref={drawRef}>
            <div className='viewport-item'>
                {/* todo 只能用"viewport"，pythagoras库里model都用的viewport取得element，model修改后这里可以修改viewportId */}
                <Viewport sceneUpdate={sceneUpdate} viewportId='viewport' viewHelperId='viewHelper-1' cameraType={systemContext.currentCameraType} />
            </div>
            <MaskLoadingModal
                visible={loading}
                height='70px'
                loadingTip={loadingTip}
            />
        </div>
    )
}